import React, { useEffect, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { LogOut, Shield, Languages, UserCog, Sun, Moon, Gamepad2 } from "lucide-react";
import Wc2026Logo from "./Wc2026Logo";
import NotificationBell from "./NotificationBell";
import { useTheme } from "../context/ThemeContext";
import { api } from "../lib/api";

const linkBase =
  "font-body text-[11px] uppercase tracking-[0.18em] px-3 py-2 border border-transparent transition-colors hover:text-[#7C3AED]";
const linkActive = "text-[#7C3AED] border-[#7C3AED]";

export default function Navbar({ activeLeague }) {
  const { user, logout } = useAuth();
  const { t, lang, toggle } = useT();
  const { theme, toggle: toggleTheme } = useTheme();
  const nav = useNavigate();
  const [classicsBadge, setClassicsBadge] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!user || user === false) { setClassicsBadge(0); return undefined; }
    const load = async () => {
      try {
        const { data } = await api.get("/classics/badge");
        if (!cancelled) setClassicsBadge(data?.pending_incoming || 0);
      } catch { /* noop */ }
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user]);

  const leagueStatus = activeLeague
    ? activeLeague.status === "open"
      ? `${t("nav.lobby")} ${activeLeague.player_count}/${activeLeague.capacity}`
      : `${activeLeague.name.toUpperCase()} — ${t("nav.locked")}`
    : t("nav.noLeague");

  return (
    <header
      data-testid="main-navbar"
      className="sticky top-0 z-40 border-b border-[#E4E4E7] dark:border-[#2A2A30] bg-[#F4F4F5]/90 dark:bg-[#09090B]/90 backdrop-blur-xl"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-4">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <div className="relative">
            <div className="absolute -inset-0.5 bg-gradient-to-br from-[#7C3AED] via-[#7C3AED]/40 to-[#00FF87] opacity-60 blur-[6px] group-hover:opacity-90 transition-opacity" aria-hidden />
            <img
              src="/logo-256.png"
              alt="ePlay"
              data-testid="brand-logo"
              className="relative w-10 h-10 rounded-lg shadow-[0_0_20px_rgba(0,229,255,0.15)] flex-shrink-0 ring-1 ring-white/10"
              width={40}
              height={40}
            />
          </div>
          <div className="leading-tight">
            <div className="flex items-center gap-2">
              <span className="font-heading text-2xl uppercase tracking-[0.04em] drop-shadow-[0_1px_0_rgba(0,0,0,0.35)]">{t("nav.title")}</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#7C3AED] border border-[#7C3AED]/40 px-1.5 py-[2px]" data-testid="nav-section-pill">
                {t("nav.section")}
              </span>
            </div>
            <div className="text-[10px] font-mono text-[#71717A]">{leagueStatus}</div>
          </div>
        </Link>

        <nav className="flex items-center gap-1 flex-wrap" data-testid="main-nav">
          <NavLink to="/dashboard" end className={({ isActive }) => `${linkBase} ${isActive ? linkActive : "text-[#09090B] dark:text-[#F4F4F5]"}`} data-testid="nav-dashboard">{t("nav.dashboard")}</NavLink>
          <NavLink to="/standings" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : "text-[#09090B] dark:text-[#F4F4F5]"}`} data-testid="nav-standings">{t("nav.standings")}</NavLink>
          <NavLink to="/leagues" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : "text-[#09090B] dark:text-[#F4F4F5]"}`} data-testid="nav-leagues">{t("nav.leagues")}</NavLink>
          <NavLink to="/wc2026" className={({ isActive }) => `${linkBase} relative ${isActive ? "text-[#FFD700] border-b-2 border-[#FFD700]" : "text-[#09090B] dark:text-[#F4F4F5] hover:text-[#FFD700]"}`} data-testid="nav-wc2026">
            <span className="inline-flex items-center gap-1.5"><Wc2026Logo variant="auto" className="w-5 h-5" alt="" /> WC 2026</span>
            {Date.now() < Date.UTC(2026, 5, 30) && (
              <span className="absolute -top-1 -right-1 px-1 rounded-full bg-[#FFD700] text-[#020617] font-mono text-[8px] font-extrabold tracking-widest" data-testid="nav-wc2026-new">NEW</span>
            )}
          </NavLink>
          <NavLink to="/classics" className={({ isActive }) => `${linkBase} relative ${isActive ? "text-[#7C3AED] border-b-2 border-[#7C3AED]" : "text-[#09090B] dark:text-[#F4F4F5]"}`} data-testid="nav-classics">
            <span className="inline-flex items-center gap-1"><Gamepad2 className="w-3 h-3" /> {t("nav.classics")}</span>
            {classicsBadge > 0 && (
              <span data-testid="nav-classics-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center px-1 rounded-full bg-[#E11D48] text-white font-mono text-[9px] font-bold">
                {classicsBadge > 9 ? "9+" : classicsBadge}
              </span>
            )}
          </NavLink>
          <NavLink to="/social" className={({ isActive }) => `${linkBase} ${isActive ? "text-[#00FF87] border-b-2 border-[#00FF87]" : "text-[#09090B] dark:text-[#F4F4F5]"}`} data-testid="nav-social">
            <span className="inline-flex items-center gap-1">{t("nav.social")}</span>
          </NavLink>
          {user && user !== false && (
            <NavLink to="/my-matches" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : "text-[#09090B] dark:text-[#F4F4F5]"}`} data-testid="nav-my-matches">{t("nav.myMatches")}</NavLink>
          )}
          {user && user !== false && (
            <NavLink to="/chat" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : "text-[#09090B] dark:text-[#F4F4F5]"}`} data-testid="nav-chat">{t("nav.chat")}</NavLink>
          )}
          {(user?.role === "admin" || user?.role === "moderator") && (
            <NavLink to="/admin" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : "text-[#09090B] dark:text-[#F4F4F5]"}`} data-testid="nav-admin">
              <span className="inline-flex items-center gap-1"><Shield className="w-3 h-3" /> {user?.role === "moderator" ? "MOD" : t("nav.admin")}</span>
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="theme-toggle-btn"
            onClick={toggleTheme}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            className="border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors inline-flex items-center gap-2"
          >
            {theme === "dark" ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />}
            <span data-testid="theme-toggle-label">{theme === "dark" ? "LIGHT" : "DARK"}</span>
          </button>

          <button
            data-testid="lang-toggle-btn"
            onClick={toggle}
            title={lang === "en" ? "تبديل إلى العربية" : "Switch to English"}
            className="border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors inline-flex items-center gap-2"
          >
            <Languages className="w-3 h-3" />
            <span data-testid="lang-toggle-label">{lang === "en" ? "AR" : "EN"}</span>
          </button>

          {user && user !== false ? (
            <>
              <NotificationBell />
              <Link
                to="/account"
                data-testid="nav-account"
                title="Account settings"
                className="border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors inline-flex items-center"
              >
                <UserCog className="w-3.5 h-3.5" />
              </Link>
              <div className="text-right leading-tight">
                <div className="font-heading uppercase text-sm" data-testid="nav-username">{user.username}</div>
                <div className="font-mono text-[10px] text-[#71717A] uppercase" data-testid="nav-gameid">{t("nav.id")} {user.game_id}</div>
              </div>
              <button
                data-testid="logout-btn"
                onClick={() => { logout(); nav("/login"); }}
                className="border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-xs uppercase tracking-widest hover:border-[#E11D48] hover:text-[#E11D48] transition-colors inline-flex items-center gap-2"
              >
                <LogOut className="w-3 h-3" /> {t("nav.exit")}
              </button>
            </>
          ) : user === false ? (
            <>
              <Link to="/login" className="border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-2 text-xs uppercase tracking-widest hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors" data-testid="nav-login">{t("nav.login")}</Link>
              <Link to="/register" className="bg-[#7C3AED] text-white px-4 py-2 text-xs uppercase tracking-widest font-heading hover:bg-[#6D28D9] transition-colors" data-testid="nav-register">{t("nav.register")}</Link>
            </>
          ) : null}
        </div>
      </div>
      <div className="pitch-accent-line" aria-hidden data-testid="nav-pitch-accent" />
    </header>
  );
}
